import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';
import '../styles/Modal.css';

function DocumentRequirementModal({ isOpen, onClose, requirement, onSuccess }) {
  const [formData, setFormData] = useState({
    document_type: '',
    document_key: '',
    name_en: '',
    name_tr: '',
    name_ru: '',
    is_required: true,
    user_type: 'driver',
    active: true,
    sort_order: 0,
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (requirement) {
      setFormData({
        document_type: requirement.document_type || '',
        document_key: requirement.document_key || '',
        name_en: requirement.name_en || '',
        name_tr: requirement.name_tr || '',
        name_ru: requirement.name_ru || '',
        is_required:
          requirement.is_required !== undefined
            ? requirement.is_required
            : true,
        user_type: requirement.user_type || 'driver',
        active: requirement.active !== undefined ? requirement.active : true,
        sort_order: requirement.sort_order || 0,
      });
    } else {
      setFormData({
        document_type: '',
        document_key: '',
        name_en: '',
        name_tr: '',
        name_ru: '',
        is_required: true,
        user_type: 'driver',
        active: true,
        sort_order: 0,
      });
    }
  }, [requirement]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    console.log(`Changing ${name} to ${type === 'checkbox' ? checked : value}`);

    // For checkboxes, ensure we're storing a boolean value
    const newValue = type === 'checkbox' ? Boolean(checked) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    console.log(
      `Updated formData.${name} to:`,
      type === 'checkbox' ? Boolean(checked) : value
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validate form data
      if (
        !formData.document_type ||
        !formData.document_key ||
        !formData.name_en ||
        !formData.name_tr ||
        !formData.name_ru
      ) {
        setError('Lütfen tüm zorunlu alanları doldurun.');
        setLoading(false);
        return;
      }

      // Convert sort_order to integer and ensure boolean values
      const dataToSubmit = {
        ...formData,
        sort_order: parseInt(formData.sort_order, 10) || 0,
        is_required: formData.is_required === true,
        active: formData.active === true,
      };

      // Log the actual values being submitted
      console.log('Submitting with exact values:');
      console.log(
        'is_required:',
        formData.is_required,
        '->',
        dataToSubmit.is_required
      );
      console.log('active:', formData.active, '->', dataToSubmit.active);

      console.log('Submitting data:', dataToSubmit);

      if (requirement) {
        // Update existing requirement
        console.log(`Updating requirement with ID ${requirement.id}`);
        console.log(
          `is_required: ${dataToSubmit.is_required}, active: ${dataToSubmit.active}`
        );

        // Create a copy of the data to submit with the ID explicitly included
        const updateData = {
          ...dataToSubmit,
          id: requirement.id, // Ensure ID is included
        };

        console.log('Final update data:', updateData);

        const { data, error } = await supabase
          .from('document_requirements')
          .update(updateData)
          .eq('id', requirement.id)
          .select();

        if (error) throw error;
        console.log('Update successful:', data);
      } else {
        // Insert new requirement
        const { error } = await supabase
          .from('document_requirements')
          .insert([dataToSubmit]);

        if (error) {
          if (error.code === '23505') {
            setError('Bu belge tipi veya anahtar zaten kullanılıyor.');
            setLoading(false);
            return;
          }
          throw error;
        }
      }

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving document requirement:', error);
      setError('Belge gereksinimi kaydedilirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content max-w-2xl">
        <div className="p-6">
          <div className="modal-header mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              {requirement
                ? 'Belge Gereksinimini Düzenle'
                : 'Yeni Belge Gereksinimi Ekle'}
            </h2>
            <button className="close-button" onClick={onClose}>
              ×
            </button>
          </div>

          <div className="modal-body">
            {error && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Belge Tipi <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="document_type"
                    value={formData.document_type}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!!requirement} // Disable editing document_type for existing requirements
                    required
                  />
                  <small className="text-gray-500">
                    Örnek: drivers_license, passport
                  </small>
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Belge Anahtarı <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="document_key"
                    value={formData.document_key}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={!!requirement} // Disable editing document_key for existing requirements
                    required
                  />
                  <small className="text-gray-500">
                    Örnek: driversLicense, taxPlate
                  </small>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    İngilizce Adı <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name_en"
                    value={formData.name_en}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Türkçe Adı <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name_tr"
                    value={formData.name_tr}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rusça Adı <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name_ru"
                    value={formData.name_ru}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Kullanıcı Tipi <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="user_type"
                    value={formData.user_type}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="driver">Sürücü</option>
                    <option value="company">Şirket</option>
                    <option value="all">Tümü</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Sıralama
                  </label>
                  <input
                    type="number"
                    name="sort_order"
                    value={formData.sort_order}
                    onChange={handleChange}
                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="form-group flex items-center">
                  <label className="inline-flex items-center mt-3">
                    <input
                      type="checkbox"
                      name="is_required"
                      checked={Boolean(formData.is_required)}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          is_required: e.target.checked,
                        }));
                        console.log(
                          'is_required changed to:',
                          e.target.checked
                        );
                      }}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2 text-gray-700">Zorunlu</span>
                  </label>
                </div>

                <div className="form-group flex items-center">
                  <label className="inline-flex items-center mt-3">
                    <input
                      type="checkbox"
                      name="active"
                      checked={Boolean(formData.active)}
                      onChange={(e) => {
                        setFormData((prev) => ({
                          ...prev,
                          active: e.target.checked,
                        }));
                        console.log('active changed to:', e.target.checked);
                      }}
                      className="form-checkbox h-5 w-5 text-blue-600"
                    />
                    <span className="ml-2 text-gray-700">Aktif</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 border text-gray-700 rounded-md hover:bg-gray-100"
                  disabled={loading}
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  disabled={loading}
                >
                  {loading
                    ? 'Kaydediliyor...'
                    : requirement
                      ? 'Güncelle'
                      : 'Kaydet'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DocumentRequirementModal;
